import { autoinject } from "aurelia-framework";
import { SearchHistoryClientService } from "../../../../../services/modules/search-history-client.service";
import { NotificationClientService, NotificationType } from "../../../../../services/basics/notification-client.service";
import { I18N } from "aurelia-i18n";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { GenericConfirmationDialogModel } from "../../../../../models/basics/generic-confirmation-dialog-model";
import { DialogServiceConstants } from "../../../../../constants/basics/dialog.constants";
import * as moment from "moment";
import { Guid } from "../../../../../utilities/guid";
import { ProjectionPropertyOrdering, SortProperty } from "../../../../../models/queries/queries.models";
import { QueriesClientService } from "../../../../../services/queries/queries-client.services";
import { Pager } from "../../../../components/pager/pager";
import * as Enumerable from "linq";

@autoinject() 
export class SearchHistoryView {
    public itemSource: any;
    public allEntriesSelected: boolean = false;
    public canRemove: boolean = false;
    public searchDateGroups: any;

    public searchHistoryPager: Pager = null;
    public count: number;
    public take = 50;
    public skip = 0;
    public sorts: SortProperty[];

    public constructor (private i18N: I18N, 
        private searchHistoryService: SearchHistoryClientService,
        private dialogClientService: DialogClientService,
        private notificationService: NotificationClientService,
        private queryService: QueriesClientService) {
        this.sorts = [
            {
                propertyName: 'searchDate',
                order: ProjectionPropertyOrdering.Descending
            } as SortProperty
        ]
    }

    getSearchTime(entity) {
        return moment(entity.searchDate).format('h:mm a');
    }

    getEntriesForGroup(group) {
        return this.itemSource.filter(x => group.groupDates.includes(x.entity.searchDate));
    }

    activate() {
        this.loadData();
    }

    async loadData() {
        await this.getItemSource();
        this.groupSearchDates();
    }

    public loadSearchHistory = (param: any) => {
        this.setResults(param);
        this.groupSearchDates();
    }

    private getItemSource() : Promise<void> {
        return new Promise<void>((resolve, reject) => { this.queryService.getData(`odata/SearchHistory?$skip=${this.skip}&$top=${this.take}&$count=true&$orderby=searchDate desc`, Guid.generateGuid()).then(response => {
            this.setResults(response);
                resolve();
            }).catch(() => {
                this.notificationService.show(this.i18N.tr('error-text'), "", NotificationType.Error);
                reject();
            });
        });
    }

    setResults(response: any) {
        this.count = parseInt(response['@odata.count']);

        let i = 0;

        this.itemSource = response ? response.value.map(x => {
                return {
                    entity: x,
                    selected: this.allEntriesSelected,
                    id: i++
                };
            }) : [];
    }

    groupSearchDates() {
        if (!this.itemSource)
            return;
            
        this.searchDateGroups = Enumerable.from<any>(this.itemSource).groupBy((x) => {
            return  moment(x.entity.searchDate).startOf('day').format()
        }).select(x => {
            return {
                displayFormat: moment(x.key()).format("dddd, MMMM Do YYYY"),
                groupDates: x.toArray().map(y => y.entity.searchDate)
            }
        }).toArray();
        
    }

    getItemRoute(entity: any) {
        if (entity && entity.entityUri == "VesselSearch") {
            return `/vessel/${entity.entityId}`;
        } else if (entity && entity.entityUri == "CompanySearch") {
            return `/company/${entity.entityId}/details`;
        }
    }

    checkCanRemove() {
        if (this.itemSource.some(x => x.selected === true)) {
            this.canRemove = true;
        } else {
            this.canRemove = false;
        }
    }

    checkSelectAll() {
        this.allEntriesSelected = this.itemSource.every(i => i.selected);
    }

    allEntriesSelectedChanged(){
        this.itemSource.forEach(x => {
            x.selected = this.allEntriesSelected
        });
        this.checkCanRemove();
        this.checkSelectAll();
    }

    selectEntry(entry: any) {
        this.itemSource.find(i => i.id === entry.id).selected = entry.selected;
        this.checkCanRemove();
        this.checkSelectAll();
    }

    removeDialog() {
        const model = new GenericConfirmationDialogModel();
        model.confirm = this.i18N.tr('delete-text');
        model.cancel = this.i18N.tr('cancel-text');
        model.messages.push(this.i18N.tr('are-you-sure-you-want-to-delete'));
        model.title = this.i18N.tr('confirm-delete');
        model.onConfirm = () => this.removeSelected();
        this.dialogClientService.show(DialogServiceConstants.URLs.GenericConfirmationDialog, model, false);
    }

    removeSelected() : Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const entriesToRemove = this.itemSource.filter(x => x.selected).map(x => {
                return x.entity.searchHistoryEntryId;
            });
            this.searchHistoryService.deleteSearchResults(entriesToRemove).then(() => {
                this.loadData();
                this.notificationService.show(this.i18N.tr('success-text'), "", NotificationType.Success);
                resolve();
            }).catch(() => {
                this.notificationService.show(this.i18N.tr('error-text'), "", NotificationType.Error);
                reject();
            })
        })
    }
}
